/**
 * Builders for the human-readable entity-link descriptions Helios sends to the
 * commentary-ui web component. Each grid panel knows its own row shape, so it
 * picks the matching builder and assembles a `entityDescriptions` map keyed by
 * "<dim>:<id>" — the same key shape the web component looks up when rendering
 * a chip.
 *
 * commentary-ws stores these strings verbatim (it is content-agnostic); the
 * templates — what fields go into a description and in what order — live here so
 * they stay consistent across panels and are unit-testable without a grid.
 */

/** Join the non-empty parts with a single space, each trimmed. */
function joinParts(
  ...parts: Array<string | number | null | undefined>
): string {
  return parts
    .map(p => (p == null ? '' : String(p).trim()))
    .filter(p => p.length > 0)
    .join(' ')
}

export type CommentaryDimension =
  | 'tran'
  | 'exttran'
  | 'break'
  | 'asset'
  | 'account'

export interface EntityDescriptionEntry {
  key: string
  label: string
}

export function describeEntity(
  dim: CommentaryDimension,
  id: string | number | null | undefined,
  label: string,
): EntityDescriptionEntry | null {
  if (id == null || id === '') return null
  return { key: `${dim}:${id}`, label }
}

export interface TransactionLabelParts {
  tranTypeCode: string | null | undefined
  amount: number | null | undefined
  units: number | null | undefined
  secIdentifier: string | null | undefined
}

export interface ClearwaterTransactionLabelParts extends TransactionLabelParts {
  entryDate: string | null | undefined
  tradeDate: string | null | undefined
}

export interface ExternalTransactionLabelParts extends TransactionLabelParts {
  tradeDate: string | null | undefined
}

export interface BreakLabelParts {
  displayName: string | null | undefined
  startDate: Date | string | null | undefined
  securityIdentifier: string | null | undefined
}

export interface AssetLabelParts {
  secIdentifier: string | null | undefined
}

export interface AccountLabelParts {
  accountName: string | null | undefined
}

function transactionLabel(
  t: TransactionLabelParts,
  date: string | null | undefined,
): string {
  // Prefer the cash amount; fall back to units when the row carries no amount.
  // Cap amount and units at 2 decimals (trailing zeros dropped) so cents-level
  // figures stay readable instead of spilling raw floating-point precision.
  const quantity =
    t.amount != null
      ? `${Number(t.amount.toFixed(2))}`
      : t.units != null
      ? `${Number(t.units.toFixed(2))} units`
      : ''
  return joinParts(t.tranTypeCode, quantity, date, t.secIdentifier)
}

export function clearwaterTransactionDescription(
  t: ClearwaterTransactionLabelParts,
): string {
  return transactionLabel(t, t.entryDate ?? t.tradeDate)
}

export function externalTransactionDescription(
  t: ExternalTransactionLabelParts,
): string {
  return transactionLabel(t, t.tradeDate)
}

/** Normalize a date (or ISO/date string) to a "yyyy-MM-dd" label. */
function dateLabel(d?: Date | string | null): string {
  if (d == null) return ''
  return d instanceof Date
    ? d.toISOString().slice(0, 10)
    : String(d).slice(0, 10)
}

export function breakDescription(b: BreakLabelParts): string {
  return joinParts(b.displayName, dateLabel(b.startDate), b.securityIdentifier)
}

export function assetDescription(a: AssetLabelParts): string {
  return joinParts(a.secIdentifier)
}

/** The account name — the id is already the chip's fallback. */
export function accountDescription(a: AccountLabelParts): string {
  return joinParts(a.accountName)
}

/**
 * Assemble an `entityDescriptions` map from per-ref entries, dropping any whose
 * label is blank. Returns `undefined` when nothing usable remains so callers
 * can omit the field entirely (and the drawer attribute stays unset).
 */
export function buildEntityDescriptions(
  entries: Array<EntityDescriptionEntry | null | undefined>,
): Record<string, string> | undefined {
  const map: Record<string, string> = {}
  for (const entry of entries) {
    const label = entry?.label?.trim()
    if (entry && label) map[entry.key] = label
  }
  return Object.keys(map).length > 0 ? map : undefined
}
